import { useCallback, useMemo, useState } from "react";
import {
  CampaignType,
  FollowerStatus,
  MassTextAttachment,
  MassTextAttachmentType,
  SubSelectionType,
} from "@markit/common.types";
import { Colors } from "../../utils/colors";
import CreatorModePanelWrapper from "../../components/CreatorDashboard/CreatorModePanelWrapper";
import { useCampaignSelector } from "../../hooks/useParametricSelector";
import { useSelector } from "react-redux";
import { getAccountSlice } from "../../redux/slices/accountSlice";
import { useLocation, useNavigate } from "react-router-dom";
import CreatorModeWrapper from "../../components/CreatorDashboard/CreatorModeWrapper";
import LoadingScreen from "../LoadingScreen";
import RectangleButton from "../../components/Buttons/RectangleButton";
import { NavigationId } from "../../navigation/AppParamList";
import { Icon } from "@iconify/react";
import { useTheme } from "../../hooks/useTheme";
import CampaignFlowPanel from "../../components/CreatorDashboard/Campaigns/CampaignFlowPanel";
import CampaignSettingsPanel from "../../components/CreatorDashboard/Campaigns/CampaignSettingsPanel";
import {
  handlePluralString,
  isDefaultTemplateAutomations,
  isPresetNoRecipientsAutomations,
} from "@markit/common.utils";
import CampaignRenameModal from "../../components/CreatorDashboard/Campaigns/Items/CampaignRenameModal";
import {
  getCampaignLinkAnalytics,
  showTextConversions,
} from "../../utils/trackingUtils";
import { useLiveUpdatingCampaign } from "../../hooks/useLiveHooks/useLiveUpdatingCampaign";
import useAsyncEffect from "../../hooks/useAsyncEffect";
import { getEventSlice } from "../../redux/slices/eventSlice";
import {
  onCreatorPanelNavigatePath,
  onDMAutomationNavigatePath,
} from "../../utils/navigationUtils";
import AutomationDefaultApplyAllModal from "../../components/CreatorDashboard/Campaigns/Items/AutomationDefaultApplyAllModal";
import Skeleton from "react-loading-skeleton";

enum CampaignDashboardTabs {
  OVERVIEW = 0,
  SETTINGS = 1,
}

const CampaignDashboard = () => {
  const { accountData, appInitialized, followersData } =
    useSelector(getAccountSlice);
  const navigate = useNavigate();
  const { theme } = useTheme();
  const events = useSelector(getEventSlice).events;
  const { navigationPath } = useLocation().state || {}; // navigationPath = path to return back to
  const campaignId = useCampaignSelector();
  const [totalClicks, setTotalClicks] = useState(0);
  const [totalConversions, setTotalConversions] = useState(0);
  const [loadingCampaign, setLoadingCampaign] = useState(true);
  const [tabValue, setTabValue] = useState(CampaignDashboardTabs.OVERVIEW);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(false);

  const {
    liveCampaign: campaign,
    liveCampaignDetail,
    isLiveCampaignError,
    loaded,
    dmAutomation,
  } = useLiveUpdatingCampaign({
    initialCampaignId: campaignId,
    userId: accountData.uid,
  });

  const styles = {
    headerRightText: {
      fontSize: 32,
    },
  };

  const subscribedFollowers = useMemo(
    () =>
      followersData.filter(
        (follower) => follower.status === FollowerStatus.SUBSCRIBED
      ),
    [followersData]
  );

  const numTotalRecipients = useMemo(() => {
    const initialText = liveCampaignDetail?.massTextDetails.find(
      (text) => text.massText.id === campaign?.initialTextId
    )?.massText;
    if (
      initialText &&
      initialText.subSelectionIds.some(
        (category) => category.type === SubSelectionType.ALL_FOLLOWERS
      )
    ) {
      return subscribedFollowers.length;
    } else {
      return liveCampaignDetail?.numRecipients ?? 0;
    }
  }, [campaign?.initialTextId, liveCampaignDetail, subscribedFollowers.length]);

  const campaignAttachments = useMemo(
    () =>
      liveCampaignDetail
        ? liveCampaignDetail.massTextDetails.reduce(
            (prev, curr) => prev.concat(curr.attachments),
            [] as MassTextAttachment[]
          )
        : [],
    [liveCampaignDetail]
  );

  const campaignHasAttachments = useMemo(() => {
    return (
      campaignAttachments.length > 1 ||
      (campaignAttachments.length === 1 &&
        campaignAttachments[0].massTextType !==
          MassTextAttachmentType.TICKET_LINK &&
        campaignAttachments[0].massTextType !==
          MassTextAttachmentType.CALENDAR_INVITE)
    );
  }, [campaignAttachments]);

  const showCampaignConversions = useMemo(
    () =>
      campaignAttachments.some((attachment) => {
        const foundEvent = events.find(
          (event) => event.id === attachment.eventId
        );
        return foundEvent && showTextConversions(foundEvent, attachment);
      }),
    [campaignAttachments, events]
  );

  const automationCampaign = useMemo(
    () => campaign?.type === CampaignType.AUTOMATION,
    [campaign?.type]
  );

  const showRenameButton = useMemo(
    () =>
      !editingCampaign &&
      campaign &&
      (campaign.type === CampaignType.CAMPAIGN ||
        (campaign.type === CampaignType.AUTOMATION && campaign.eventId)),
    [campaign, editingCampaign]
  );

  const backOnPress = useCallback(() => {
    if (editingCampaign) {
      setEditingCampaign(false);
      return;
    }
    // If navigated to automation from texting tab explicitly, navigate back to that tab
    if (navigationPath) {
      onCreatorPanelNavigatePath(
        navigate,
        navigationPath,
        campaign?.type === CampaignType.CAMPAIGN ? 0 : 1
      );
      return;
    }
    // Automation campaigns always belong to a DM automation or an event —
    // default back to that product/event dashboard
    if (campaign?.type === CampaignType.AUTOMATION) {
      if (dmAutomation) {
        onDMAutomationNavigatePath(navigate, dmAutomation.id);
        return;
      }
      if (campaign.eventId) {
        navigate(`/e/${campaign.eventId}/dashboard`, {
          state: { eventId: campaign.eventId },
        });
        return;
      }
    }
    // Standard text campaigns navigate back to the home texting tab
    onCreatorPanelNavigatePath(
      navigate,
      NavigationId.HOME_TEXTING,
      campaign?.type === CampaignType.CAMPAIGN ? 0 : 1
    );
  }, [editingCampaign, navigationPath, campaign, dmAutomation, navigate]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useAsyncEffect(async () => {
    if (
      !appInitialized ||
      !loadingCampaign ||
      !campaign ||
      !liveCampaignDetail
    ) {
      return;
    }

    const { totalOpens, totalConversions } = await getCampaignLinkAnalytics(
      liveCampaignDetail.massTextDetails,
      campaign
    );
    setTotalClicks(totalOpens);
    setTotalConversions(totalConversions);
    setLoadingCampaign(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountData.uid, appInitialized, loaded]);

  if (!loaded || !campaign || !liveCampaignDetail) {
    return (
      <LoadingScreen
        isLoadingPage={loadingCampaign || !loaded}
        error={isLiveCampaignError}
      />
    );
  }

  return (
    <CreatorModeWrapper hideSidebar preventScroll>
      <CreatorModePanelWrapper
        preventScroll
        title={
          <div className="ColumnNormal" style={{ gap: 7 }}>
            <div className="AlignedRow" style={{ gap: 20 }}>
              <RectangleButton
                buttonLabel={
                  <span className="bodyMedium">
                    {editingCampaign ? "Exit" : "Back"}
                  </span>
                }
                onPress={backOnPress}
                altBorderRadius={100}
                altPaddingHorz={12}
                altPaddingVert={10}
                width={65}
                containerStyles={{
                  border: `1px solid ${Colors.GRAY1}`,
                }}
                altColor={Colors.WHITE1}
                altTextColor={Colors.BLACK}
                iconLeft={
                  <div style={{ cursor: "pointer" }}>
                    <Icon icon={"ion:chevron-left"} height={10} />
                  </div>
                }
              />
              <div className="AlignedRow" style={{ gap: 7, maxWidth: 1000 }}>
                <span className="TwoLineText">
                  {editingCampaign
                    ? `Edit ${automationCampaign ? "Automation" : "Campaign"}`
                    : campaign.title}
                </span>
                {showRenameButton ? (
                  <div>
                    <Icon
                      icon={"ion:create"}
                      height={22}
                      style={{ ...theme.LabelText, cursor: "pointer" }}
                      onClick={() => setShowRenameModal(true)}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        }
        headerContainerStyles={{ alignItems: "center" }}
        headerRight={
          !editingCampaign ? (
            isDefaultTemplateAutomations(campaign.id) ? (
              <AutomationDefaultApplyAllModal campaignId={campaign.id} />
            ) : loadingCampaign ? (
              <Skeleton width={200} height={50} />
            ) : (
              <div className="AlignedRow" style={{ gap: 30 }}>
                {!isPresetNoRecipientsAutomations(campaign.id) &&
                !isDefaultTemplateAutomations(campaign.id) ? (
                  <div className="ColumnCentering">
                    <span style={styles.headerRightText}>
                      {numTotalRecipients !== 0 || automationCampaign
                        ? numTotalRecipients
                        : "--"}
                    </span>
                    <span
                      className="smallBodySubtext"
                      style={{ textAlign: "center", whiteSpace: "nowrap" }}
                    >
                      {automationCampaign
                        ? "Started Flow"
                        : `Total ${handlePluralString(
                            "Recipient",
                            numTotalRecipients
                          )}`}
                    </span>
                  </div>
                ) : null}
                {campaignHasAttachments &&
                !isPresetNoRecipientsAutomations(campaignId) &&
                !isDefaultTemplateAutomations(campaignId) ? (
                  <>
                    <div className="ColumnCentering">
                      <span style={styles.headerRightText}>{totalClicks}</span>
                      <span
                        className="smallBodySubtext"
                        style={{ textAlign: "center", whiteSpace: "nowrap" }}
                      >
                        Total {handlePluralString("Click", totalClicks)}
                      </span>
                    </div>
                    {showCampaignConversions ? (
                      <div className="ColumnCentering">
                        <span style={styles.headerRightText}>
                          {totalConversions}
                        </span>
                        <span
                          className="smallBodySubtext"
                          style={{ textAlign: "center", whiteSpace: "nowrap" }}
                        >
                          Total{" "}
                          {handlePluralString("Conversion", totalConversions)}
                        </span>
                      </div>
                    ) : null}
                  </>
                ) : null}
              </div>
            )
          ) : undefined
        }
        headerTabs={
          !editingCampaign
            ? {
                tabNames: ["Overview", "Settings"],
                tabValue: tabValue,
                setTabValue: setTabValue,
              }
            : undefined
        }
      >
        {tabValue === CampaignDashboardTabs.OVERVIEW ? (
          <CampaignFlowPanel
            campaign={campaign}
            massTextDetails={liveCampaignDetail.massTextDetails}
            totalRecipients={numTotalRecipients}
            editingCampaign={editingCampaign}
            setEditingCampaign={setEditingCampaign}
            dmAutomation={dmAutomation}
          />
        ) : (
          <CampaignSettingsPanel
            campaign={campaign}
            massTexts={liveCampaignDetail.massTextDetails.map(
              (text) => text.massText
            )}
          />
        )}
      </CreatorModePanelWrapper>
      {showRenameModal ? (
        <CampaignRenameModal
          campaign={campaign}
          showModal={showRenameModal}
          setShowModal={setShowRenameModal}
        />
      ) : null}
    </CreatorModeWrapper>
  );
};

export default CampaignDashboard;
